import type { DashboardSaveV2Request, GapProductV2 } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrency } from "../../../../../lib/money";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { CurrencyField, DateField, DropdownField, TextField } from "./Fields";
import {
  buildGapDefaultValues,
  GAP_FORM_NUMBER_CODES,
  GAP_FORM_NUMBER_NOT_LISTED,
  GAP_FORM_NUMBER_SELECT_AN_OPTION,
  gapInformationSchema,
  toGapSaveRequest,
} from "./GapInformation.schema";
import type { GapAccess } from "./productAccess";
import { type OriginalRow, ProductOriginalsModal } from "./ProductOriginalsModal";
import { groupViolations } from "./productViolations";
import { withOption } from "./withOption";

interface GapInformationProperties {
  /** `additionalProducts.gap` off the dashboard response. */
  readonly gap?: GapProductV2;
  /** `additionalProducts.metadata.tpapGap` — selects the third-party (TPAP) layout. */
  readonly tpapGap?: boolean;
  /** Resolved per-field editability (spec 7.2); defaults to everything locked. */
  readonly access?: GapAccess;
  /** `additionalProducts.contractGap` — the dealer's RISC values shown in the P4 popup. */
  readonly contractGap?: GapProductV2;
}

const LOCKED: GapAccess = {
  headerLink: false,
  vendorDropdown: false,
  vendorEnabled: false,
  addendumPrefix: false,
  addendumNumber: false,
  sellingPrice: false,
  term: false,
  amountFinanced: false,
  effectiveDate: false,
  formNumber: false,
  anyEditable: false,
};

/** BFF violation field -> this form's field. */
const VIOLATION_FIELDS = {
  vendorId: "tpapVendor",
  vendorName: "tpapVendor",
  addendumNo: "addendumNumber",
  sellingPrice: "sellingPrice",
  term: "termCoverage",
  amountFinanced: "amountFinanced",
  effectiveDate: "effectiveDate",
  formNo: "formNumber",
} as const;

/**
 * RHF + Zod, same pattern as VehicleServiceContractInformation.tsx — see
 * GapInformation.schema.ts for the validation rules. Addendum Number is
 * required regardless of `tpapGap`; it renders as the same State Prefix/Number
 * pair in both the non-TPAP and TPAP blocks below.
 *
 * Registered with the shared save orchestrator as "gap", contributing only
 * `additionalProducts.gap` — the orchestrator merges `additionalProducts` per
 * product, so this doesn't clobber the VSC section's contribution.
 */
export function GapInformation({
  gap,
  tpapGap: isTpapGap = false,
  access = LOCKED,
  contractGap,
}: GapInformationProperties) {
  const { t } = useTranslation();
  const [originalsOpen, setOriginalsOpen] = useState(false);
  const violations = groupViolations(gap?.validations, VIOLATION_FIELDS);
  const form = useForm({
    resolver: zodResolver(gapInformationSchema),
    mode: "onBlur",
    defaultValues: buildGapDefaultValues(gap, isTpapGap),
  });
  const { control } = form;
  const tpapGap = useController({ control, name: "tpapGap" });
  const vendorDisplay = useController({ control, name: "vendorDisplay" });
  const tpapVendor = useController({ control, name: "tpapVendor" });
  const addendumStatePrefix = useController({ control, name: "addendumStatePrefix" });
  const addendumNumber = useController({ control, name: "addendumNumber" });
  const sellingPrice = useController({ control, name: "sellingPrice" });
  const termCoverage = useController({ control, name: "termCoverage" });
  const amountFinanced = useController({ control, name: "amountFinanced" });
  const effectiveDate = useController({ control, name: "effectiveDate" });
  const formNumber = useController({ control, name: "formNumber" });

  const [saveError, setSaveError] = useState<string>();

  useRegisterSaveableSection("gap", {
    // Pre-approval and locked cards skip the required-but-empty highlighting (spec 5.1).
    validate: async () => !access.anyEditable || form.trigger(),
    getPayload: (): Partial<DashboardSaveV2Request> =>
      // Nothing to write for a locked card, or when the deal has no GAP and isn't a TPAP one.
      !access.anyEditable || !(gap || isTpapGap)
        ? {}
        : { additionalProducts: { gap: toGapSaveRequest(form.getValues(), gap) } },
    onSaved: () => {
      setSaveError(undefined);
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  // Vendor changes stay disabled until a lookup supplies writable vendor IDs.
  const tpapVendorOptions = withOption([], gap?.vendorName).map((vendor) => ({
    value: vendor,
    label: vendor,
  }));
  const formNumberOptions = [
    { value: GAP_FORM_NUMBER_SELECT_AN_OPTION, label: t("vehicle.gap.selectAnOption") },
    ...withOption(GAP_FORM_NUMBER_CODES, gap?.formNo).map((code) => ({ value: code, label: code })),
    { value: GAP_FORM_NUMBER_NOT_LISTED, label: t("vehicle.gap.formNotListed") },
  ];

  // Validates on change rather than blur (like CPO Date/VSC's Form Number) —
  // selecting an option doesn't blur the trigger, so `mode: "onBlur"` alone
  // would never reveal the required error.
  function handleFormNumberChange(value: string) {
    formNumber.field.onChange(value);
    void form.trigger("formNumber");
  }

  const originalRows: OriginalRow[] = [
    { label: t("vehicle.gap.vendor"), value: contractGap?.vendorName },
    {
      label: t("vehicle.gap.sellingPrice"),
      value: contractGap?.sellingPrice == null ? undefined : formatCurrency(contractGap.sellingPrice),
    },
    { label: t("vehicle.gap.termCoverage"), value: contractGap?.term?.toString() },
    {
      label: t("vehicle.gap.amountFinanced"),
      value: contractGap?.amountFinanced == null ? undefined : formatCurrency(contractGap.amountFinanced),
    },
    { label: t("vehicle.gap.effectiveDate"), value: contractGap?.effectiveDate },
    { label: t("vehicle.gap.formNumber"), value: contractGap?.formNo },
  ];

  return (
    <div>
      <SubsectionHeader>
        {access.headerLink ? (
          <button type="button" className="underline underline-offset-2" onClick={() => setOriginalsOpen(true)}>
            {t("vehicle.gap.heading")}
          </button>
        ) : (
          t("vehicle.gap.heading")
        )}
      </SubsectionHeader>
      <ProductOriginalsModal
        open={originalsOpen}
        onOpenChange={setOriginalsOpen}
        title={t("vehicle.gap.heading")}
        rows={originalRows}
      />
      {violations.general.map((message) => (
        <p key={message} role="alert" className="text-warning text-2xs px-3 pt-2">
          {message}
        </p>
      ))}
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="px-(--spacing-3) py-(--spacing-3) gap-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
            {access.vendorDropdown ? (
              <DropdownField
                label={t("vehicle.gap.vendor")}
                value={tpapVendor.field.value}
                options={tpapVendorOptions}
                error={violations.byField.tpapVendor}
                disabled
              />
            ) : (
              <TextField
                label={t("vehicle.gap.vendor")}
                value={vendorDisplay.field.value}
                error={violations.byField.tpapVendor}
                disabled
              />
            )}
            <div className="gap-(--spacing-2) grid grid-cols-2">
              <TextField
                label={t("vehicle.gap.addendumStatePrefix")}
                value={addendumStatePrefix.field.value}
                onChange={addendumStatePrefix.field.onChange}
                onBlur={addendumStatePrefix.field.onBlur}
                error={addendumStatePrefix.fieldState.error}
                disabled={!access.addendumPrefix}
                maxLength={6}
                uppercase
              />
              <TextField
                label={t("vehicle.gap.addendumNumber")}
                value={addendumNumber.field.value}
                onChange={addendumNumber.field.onChange}
                onBlur={addendumNumber.field.onBlur}
                error={addendumNumber.fieldState.error ?? violations.byField.addendumNumber}
                disabled={!access.addendumNumber}
                maxLength={11}
                uppercase
              />
            </div>
            <CurrencyField
              label={t("vehicle.gap.sellingPrice")}
              value={sellingPrice.field.value}
              onChange={(value) => sellingPrice.field.onChange(value ?? null)}
              onBlur={sellingPrice.field.onBlur}
              error={sellingPrice.fieldState.error ?? violations.byField.sellingPrice}
              disabled={!access.sellingPrice}
            />
            {tpapGap.field.value && (
              <>
                <TextField
                  label={t("vehicle.gap.termCoverage")}
                  value={termCoverage.field.value}
                  onChange={(value) => termCoverage.field.onChange(value.replaceAll(/\D/g, ""))}
                  onBlur={termCoverage.field.onBlur}
                  error={termCoverage.fieldState.error ?? violations.byField.termCoverage}
                  disabled={!access.term}
                  maxLength={3}
                />
                <CurrencyField
                  label={t("vehicle.gap.amountFinanced")}
                  value={amountFinanced.field.value}
                  onChange={(value) => amountFinanced.field.onChange(value ?? null)}
                  onBlur={amountFinanced.field.onBlur}
                  error={amountFinanced.fieldState.error ?? violations.byField.amountFinanced}
                  disabled={!access.amountFinanced}
                />
                <DateField
                  label={t("vehicle.gap.effectiveDate")}
                  value={effectiveDate.field.value}
                  onChange={effectiveDate.field.onChange}
                  onBlur={effectiveDate.field.onBlur}
                  error={effectiveDate.fieldState.error ?? violations.byField.effectiveDate}
                  disabled={!access.effectiveDate}
                />
                <DropdownField
                  label={t("vehicle.gap.formNumber")}
                  value={formNumber.field.value}
                  onChange={handleFormNumberChange}
                  onBlur={formNumber.field.onBlur}
                  error={formNumber.fieldState.error ?? violations.byField.formNumber}
                  options={formNumberOptions}
                  disabled={!access.formNumber}
                />
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
